import {
  DEFAULT_MAX_ACCESS_DURATION,
  DEFAULT_STANDING_ACCESS_DURATION,
} from "@p0security/shared/configuration/constant";
import {
  sortApprovalOptions,
  timeUnitLabelOptions,
  timeUnitLabels,
} from "@p0security/shared/permission-requests/util";
import { printUserFriendlyDuration } from "@p0security/shared/permission-requests/util";
import {
  Alert,
  Col,
  Divider,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import { useAuthFetch } from "components/Login/hook";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { handleErrorWithToastMessage } from "utils/error";

import { useDurationSettings } from "./useDurationSettings";

const style: React.CSSProperties = { padding: "8px 0", flex: "1  1  0px" };

export const StandingAccessDuration: React.FC = () => {
  const [time, setTime] = useState<number | null>(1);
  const [unit, setUnit] = useState<keyof typeof timeUnitLabels>("m");

  const optionsDoc = useDurationSettings();

  const maxAccessDuration = useMemo(() => {
    return (
      optionsDoc.doc?.data.maxAccessDuration || DEFAULT_MAX_ACCESS_DURATION
    );
  }, [optionsDoc.doc]);

  const standingAccessDuration = useMemo(() => {
    return (
      optionsDoc.doc?.data.standingAccessDuration ||
      DEFAULT_STANDING_ACCESS_DURATION
    );
  }, [optionsDoc.doc]);

  useEffect(() => {
    setTime(standingAccessDuration.time);
    setUnit(standingAccessDuration.unit);
  }, [standingAccessDuration]);

  const standingAccessExceedsMaxAccess = useMemo(() => {
    return sortApprovalOptions(standingAccessDuration, maxAccessDuration) > 0;
  }, [maxAccessDuration, standingAccessDuration]);

  const authFetch = useAuthFetch(handleErrorWithToastMessage);

  const setStandingAccessDuration = useCallback(async () => {
    if (time && unit) {
      const response = authFetch(`settings/standing-access-duration`, {
        json: { time, unit },
        method: "PUT",
      });
      if (!response) return;
      message.success("Standing access duration set");
    }
  }, [time, unit, authFetch]);

  return (
    <div>
      <Typography.Title level={4}>Standing Access Duration</Typography.Title>
      <div>
        <Typography.Text type="secondary">
          Set the standing access duration for persistent access requests.
        </Typography.Text>
        {standingAccessExceedsMaxAccess && (
          <Alert
            message="Standing Access Duration exceeds the Maximum Access Duration"
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            description={`Persistent access requests will automatically default to the minimum allowable duration of ${printUserFriendlyDuration(
              maxAccessDuration
            )}.`}
          />
        )}
        <br />
        {optionsDoc.loading ? (
          <Skeleton.Input active block={true} />
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={24}>
              <div style={style}>
                <Typography.Text>Standing Access Duration: </Typography.Text>
                <Space>
                  <Space.Compact>
                    <InputNumber min={1} value={time} onChange={setTime} />
                    <Select
                      options={timeUnitLabelOptions}
                      value={unit}
                      onChange={setUnit}
                      style={{ width: 97 }}
                    />
                  </Space.Compact>
                  <Divider />
                  <AuthzButton
                    roles={["owner"]}
                    type="primary"
                    onClick={setStandingAccessDuration}
                  >
                    Set
                  </AuthzButton>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
